import { ChevronLeft, ChevronRight, Clock3, RefreshCw, Trophy, UserRound } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDivisionLabel, ONLINE_DIVISIONS, type OnlineDivision } from '../online/league';
import { getRankingFreshness } from '../online/rankings';
import {
  listOnlineLeaderboard,
  listOnlineSeasons,
  type OnlineLeaderboardPage,
  type OnlineSeasonRow,
} from '../services/onlineLeague';

type RankingScope = 'GLOBAL' | OnlineDivision;

export function OnlineRankingsPanel({ currentSeasonId, ownPublicPlayerId, onOpenProfile }: {
  currentSeasonId?: string;
  ownPublicPlayerId?: string;
  onOpenProfile(publicPlayerId: string): void;
}) {
  const [seasons, setSeasons] = useState<OnlineSeasonRow[]>([]);
  const [seasonId, setSeasonId] = useState(currentSeasonId ?? '');
  const [scope, setScope] = useState<RankingScope>('GLOBAL');
  const [page, setPage] = useState(1);
  const [ranking, setRanking] = useState<OnlineLeaderboardPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void listOnlineSeasons().then((items) => {
      if (cancelled) return;
      setSeasons(items);
      setSeasonId((value) => value || currentSeasonId || items[0]?.id || '');
    }).catch((reason) => {
      if (!cancelled) setError(reason instanceof Error ? reason.message : 'No se han podido cargar las temporadas.');
    });
    return () => { cancelled = true; };
  }, [currentSeasonId]);

  const loadRanking = useCallback(async (force = false) => {
    if (!seasonId) return;
    if (force) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const data = await listOnlineLeaderboard({
        seasonId,
        division: scope === 'GLOBAL' ? undefined : scope,
        page,
        force,
      });
      setRanking(data);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'No se ha podido cargar la clasificación.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [page, scope, seasonId]);

  useEffect(() => { void loadRanking(); }, [loadRanking]);

  const selectedSeason = seasons.find((season) => season.id === seasonId);
  const freshness = useMemo(() => getRankingFreshness(ranking?.updatedAt ?? null), [ranking?.updatedAt]);
  const positionLabel = scope === 'GLOBAL' ? 'Global' : getDivisionLabel(scope);

  return <section className="online-ranking-shell">
    <div className="online-ranking-toolbar card">
      <div><span className="card-label"><Trophy size={15} /> CLASIFICACIONES</span><h2>Ranking {positionLabel.toLowerCase()}</h2><p>Orden público: división, puntos, victorias, momento en que se alcanzaron los puntos e ID público estable.</p></div>
      <div className="online-ranking-controls">
        <label>Temporada<select value={seasonId} onChange={(event) => { setSeasonId(event.target.value); setPage(1); }}>{seasons.map((season) => <option value={season.id} key={season.id}>{season.name} · {season.status === 'ACTIVE' ? 'activa' : season.status.toLowerCase()}</option>)}</select></label>
        <button className="ghost-action" disabled={!seasonId || refreshing} onClick={() => void loadRanking(true)}><RefreshCw className={refreshing ? 'spin' : ''} size={14} /> Actualizar</button>
      </div>
    </div>

    <nav className="online-division-filters" aria-label="Filtrar clasificación por división">
      <button className={scope === 'GLOBAL' ? 'active' : ''} onClick={() => { setScope('GLOBAL'); setPage(1); }}>Global</button>
      {ONLINE_DIVISIONS.map((division) => <button className={scope === division ? 'active' : ''} onClick={() => { setScope(division); setPage(1); }} key={division}>{getDivisionLabel(division)}</button>)}
    </nav>

    {error && <div className="online-message online-message--error" role="alert"><span>{error}</span><button onClick={() => void loadRanking(true)}>Reintentar</button></div>}
    <div className="online-ranking-table card" aria-busy={loading}>
      <header><div><b>{selectedSeason?.name ?? 'Temporada online'}</b><small>{ranking?.total ?? 0} entrenadores clasificados</small></div><span className={`online-freshness ${freshness}`}><Clock3 size={13} /> {freshness === 'live' ? 'Datos al día' : freshness === 'recent' ? 'Actualización reciente' : 'Actualización pendiente'}</span></header>
      <div className="online-ranking-head"><span>#</span><span>Entrenador</span><span>División</span><span>Puntos</span><span>V / E / D</span><span>Victorias</span></div>
      {loading && !ranking ? <div className="online-ranking-loading"><RefreshCw className="spin" size={20} /> Calculando posiciones…</div> : ranking?.items.map((entry) => {
        const position = scope === 'GLOBAL' ? entry.globalPosition : entry.divisionPosition;
        const own = entry.publicPlayerId === ownPublicPlayerId;
        return <article className={`online-ranking-row ${own ? 'own' : ''}`} key={entry.publicPlayerId}>
          <strong className="online-ranking-position">{position}</strong>
          <button className="online-ranking-player" onClick={() => onOpenProfile(entry.publicPlayerId)}><span>{entry.alias.slice(0, 2).toUpperCase()}</span><b>{entry.alias}{own && <small>TÚ</small>}</b><UserRound size={15} /></button>
          <span className={`division-pill division-${entry.division.toLowerCase()}`}>{getDivisionLabel(entry.division)}</span>
          <strong>{entry.divisionPoints}</strong>
          <span>{entry.wins} / {entry.draws} / {entry.losses}</span>
          <span>{entry.winRate.toFixed(1)}%</span>
        </article>;
      })}
      {!loading && !ranking?.items.length && <div className="online-ranking-empty">Todavía no hay entrenadores en esta clasificación.</div>}
      <footer><small>Página {ranking?.page ?? page} de {ranking?.totalPages ?? 1} · caché de lectura de 30 segundos</small><div><button disabled={page <= 1 || loading} onClick={() => setPage((value) => Math.max(1, value - 1))}><ChevronLeft size={15} /> Anterior</button><button disabled={loading || page >= (ranking?.totalPages ?? 1)} onClick={() => setPage((value) => value + 1)}>Siguiente <ChevronRight size={15} /></button></div></footer>
    </div>
  </section>;
}
